/**
 * Shared chrome primitives used by every screen:
 *   - Sidebar  : left-rail nav
 *   - Topbar   : global header with site picker, search, theme
 *   - StatusPill: status → color mapping
 *   - Toasts   : transient notifications
 *   - CmdPalette: ⌘K fuzzy search
 *   - HelpOverlay: ? keyboard shortcut sheet
 */

import { useEffect, useRef, useState } from 'react';
import { Icons, type IconSpec } from 'turbotwin-sdk/icons';
import { Icon } from './Icon';
import { PortalIcons } from './portalIcons';
import type { NotificationItem, Site, Toast, ViewKey } from '@/data/types';
import { LauncherButton } from './LauncherButton';
import { DownloadBanner } from './DownloadBanner';
import { useLocalSignOut } from '../contexts/AuthProvider';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { GatedButton } from './GatedButton';

// Platform-aware shortcut labels. The handlers accept both metaKey and
// ctrlKey (App.tsx `mod`), but the hints hardcoded ⌘ — wrong for the
// Windows/Linux majority of mine-site coordinators, who'd try to find a
// Command key. Mac keeps the bare-chord convention (⌘K); everyone else
// gets the Ctrl+K spelling.
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
export const MOD_LABEL = IS_MAC ? '⌘' : 'Ctrl';
export const modChord = (k: string) => (IS_MAC ? `⌘${k}` : `Ctrl+${k}`);

/** The pages the sidebar and the command palette link to. */
type NavKey = 'live' | 'trainee' | 'workforce' | 'cohort-builder' | 'cohorts' | 'review-queue'
  | 'members' | 'leadership' | 'orientations' | 'orientation-editor' | 'settings';

/** One vocabulary icon per page, shared by the sidebar and the command palette. */
const NAV_ICON: Record<NavKey, IconSpec> = {
  live: PortalIcons.view.liveSessions,
  trainee: PortalIcons.view.myOrientations,
  workforce: PortalIcons.view.workforce,
  'cohort-builder': PortalIcons.view.orientationAssignment,
  cohorts: PortalIcons.view.cohorts,
  'review-queue': PortalIcons.view.reviewQueue,
  members: Icons.view.access,
  leadership: Icons.view.dashboard,
  orientations: PortalIcons.view.orientations,
  'orientation-editor': PortalIcons.view.orientationEditor,
  settings: Icons.action.settings,
};

type SidebarProps = {
  view: ViewKey;
  onNav: (v: ViewKey) => void;
  site?: Site;
  /** Whether the user's rights open this page (lib/siteAccess). */
  canOpen: (v: ViewKey) => boolean;
  /** Icon-rail mode: labels/hints hidden, tooltips carry the names. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Picks the wordmark variant; the drawer owns the brand now. */
  theme: 'dark' | 'light';
};
export function Sidebar({ view, onNav, site, canOpen, collapsed, onToggleCollapsed, theme }: SidebarProps) {
  const items: Array<{
    g: string;
    rows: Array<[NavKey, string, string | null, boolean?]>;
  }> = [
    { g: 'Workspace', rows: [
      ['live', 'Live Sessions', null, !canOpen('live')],
      ['trainee', 'My Orientations', null],
    ]},
    { g: 'People', rows: [
      ['workforce', 'Workforce', null, !canOpen('workforce')],
      ['cohort-builder', 'Orientation Assignment', null, !canOpen('cohort-builder')],
      ['cohorts', 'Cohorts', null, !canOpen('cohorts')],
      ['review-queue', 'Review Queue', null, !canOpen('review-queue')],
      ['members', 'Members', null, !canOpen('members')],
    ]},
    { g: 'Insights', rows: [
      ['leadership', 'Leadership', null, !canOpen('leadership')],
    ]},
    { g: 'Authoring', rows: [
      ['orientations', 'Orientations', null, !canOpen('orientations')],
      ['orientation-editor', 'Orientation Editor', null, !canOpen('orientation-editor')],
    ]},
    { g: 'Account', rows: [
      ['settings', 'Settings', null],
    ]},
  ];

  return (
    <aside className={'sidebar' + (collapsed ? ' collapsed' : '')}>
      {/* Arrow chip riding the sidebar's edge line, level with the brand. */}
      <button
        className="nav-edge-toggle"
        onClick={onToggleCollapsed}
        title={(collapsed ? 'Expand menu' : 'Collapse menu') + ` (${modChord('B')})`}
        aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
      >
        <Icon of={collapsed ? PortalIcons.action.chevronRight : PortalIcons.action.chevronLeft} size={12} />
      </button>
      <div className="sidebar-brand" title={collapsed ? 'MineTwin.ai' : undefined}>
        {collapsed ? (
          <span className="brand-mark">
            <img src="/images/favicon.png" alt="MineTwin.ai" />
          </span>
        ) : (
          <img
            src={theme === 'dark' ? '/images/logo-dark.png' : '/images/logo-Light.png'}
            alt="MineTwin.ai"
            className="brand-logo"
          />
        )}
      </div>
      <div className="sidebar-scroll">
        {items
          .map(g => ({ ...g, rows: g.rows.filter(r => !r[3]) }))
          .filter(g => g.rows.length > 0)
          .map(g => (
            <div className="nav-group" key={g.g}>
              {collapsed ? <div className="nav-sep" /> : <div className="nav-hint">{g.g}</div>}
              {g.rows.map(r => (
                <button
                  key={r[0]}
                  className={'nav-item ' + (view === r[0] ? 'active' : '')}
                  onClick={() => onNav(r[0])}
                  title={collapsed ? r[1] : undefined}
                >
                  <span className="ic"><Icon of={NAV_ICON[r[0]]} size={15} /></span>
                  {!collapsed && <span className="lbl">{r[1]}</span>}
                  {!collapsed && r[2] && <span className={'badge ' + (r[0] === 'live' ? 'alert' : '')}>{r[2]}</span>}
                </button>
              ))}
            </div>
          ))}
        {site && !collapsed && (
          <div className="site-card">
            <div className="label">This Site</div>
            <div className="site">
              <span className="status-dot" /> {site.name}
            </div>
          </div>
        )}
        {site && collapsed && (
          <div className="site-mini" title={`This site: ${site.name}`}>
            <span className="status-dot" />
          </div>
        )}
      </div>
    </aside>
  );
}

type TopbarProps = {
  site?: Site;
  sites: Site[];
  siteIndex: number;
  onSiteChange: (index: number) => void;
  onOpenCmd: () => void;
  onOpenHelp: () => void;
  onOpenBugReport: () => void;
  onToggleTheme: () => void;
  theme: 'dark' | 'light';
  user: { name: string; role: string; initials: string; email: string };
  onNav: (v: ViewKey, payload?: unknown) => void;
  /** Session feed behind the bell (retained toasts, newest first). */
  notifications: NotificationItem[];
  unreadCount: number;
  onMarkNotificationsRead: () => void;
  onClearNotifications: () => void;
};

/** "just now" / "4m ago" / "2h ago" — enough resolution for a session feed. */
function timeAgo(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

const NOTIFICATION_KIND_COLOR: Record<string, string> = {
  good: 'var(--good)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  info: 'var(--navy, var(--muted))',
};

export function Topbar({ site, sites, siteIndex, onSiteChange, onOpenCmd, onOpenHelp, onOpenBugReport, onToggleTheme, theme, user, onNav: _onNav, notifications, unreadCount, onMarkNotificationsRead, onClearNotifications }: TopbarProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  useOutsideClick(profileRef, profileOpen, () => setProfileOpen(false));
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  useOutsideClick(notifRef, notifOpen, () => setNotifOpen(false));
  // "Open marks read" must also cover notifications that ARRIVE while the
  // popover is open — including the durable backlog landing after a click
  // that beat the subscription. The visible popover is the read receipt.
  useEffect(() => {
    if (notifOpen && unreadCount > 0) onMarkNotificationsRead();
  }, [notifOpen, unreadCount, onMarkNotificationsRead]);
  const [siteOpen, setSiteOpen] = useState(false);
  const siteRef = useRef<HTMLDivElement>(null);
  useOutsideClick(siteRef, siteOpen, () => setSiteOpen(false));
  const signOut = useLocalSignOut();

  return (
    <header className="topbar">
      <div ref={siteRef} style={{ position: 'relative' }}>
        <GatedButton
          className="site-picker"
          onClick={() => setSiteOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={siteOpen}
          reason={sites.length === 0
            ? 'No sites assigned to your account. Ask an org admin to grant access in TurboTwin.'
            : null}
          hint={`${sites.length} site${sites.length === 1 ? '' : 's'} available`}
          style={{ background: 'transparent', border: 0, padding: 0, cursor: sites.length === 0 ? 'not-allowed' : 'pointer' }}
        >
          <span className="label">Site</span>
          <span className="val" style={{ color: sites.length === 0 ? 'var(--muted)' : undefined }}>
            {site?.name ?? 'No sites'}
          </span>
          <span className="chev"><Icon of={PortalIcons.action.expand} size={12} /></span>
        </GatedButton>
        {siteOpen && sites.length > 0 && (
          <div
            role="listbox"
            aria-label="Choose site"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 6,
              minWidth: 260,
              maxHeight: 320,
              overflowY: 'auto',
              background: 'var(--bg-1)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r, 6px)',
              padding: 6,
              zIndex: 30,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              color: 'var(--text)',
              fontSize: 12.5,
            }}
          >
            {sites.map((s, i) => {
              const selected = i === siteIndex;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onSiteChange(i);
                    setSiteOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 10px',
                    background: selected ? 'var(--panel-2)' : 'transparent',
                    border: 0,
                    borderRadius: 'var(--r, 6px)',
                    color: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span
                    className="av amber"
                    style={{ width: 26, height: 26, fontSize: 10, flex: '0 0 auto' }}
                  >
                    {s.abbr}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600 }}>{s.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>
                      {s.id}
                    </span>
                  </span>
                  {selected && (
                    <span style={{ color: 'var(--gold, #D4952B)', fontSize: 11 }}>
                      current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="search-box" onClick={onOpenCmd} role="button" aria-label="Search">
        <Icon of={Icons.action.search} size={14} />
        <span className="ph">Search workers, stops, reports…</span>
        <span className="kbd">{modChord('K')}</span>
      </div>
      <div className="top-right">
        <button type="button" className="icon-btn" title="Report a problem" onClick={onOpenBugReport} aria-label="Report a problem">
          <Icon of={Icons.action.reportBug} size={16} />
        </button>
        <button type="button" className="icon-btn" title="Keyboard shortcuts" onClick={onOpenHelp} aria-label="Keyboard shortcuts">
          <Icon of={PortalIcons.action.keyboardShortcuts} size={16} />
        </button>
        <button type="button" className="icon-btn" title="Toggle light / dark" onClick={onToggleTheme} aria-label="Toggle theme">
          <Icon of={theme === 'dark' ? PortalIcons.action.themeDark : PortalIcons.action.themeLight} size={16} />
        </button>
        <LauncherButton />
        {/* #194: mounted in the chrome so an in-flight download is visible
            on every page, not only inside the (closable) launcher popover. */}
        <DownloadBanner />
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="icon-btn"
            title={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-haspopup="dialog"
            aria-expanded={notifOpen}
            onClick={() => {
              setNotifOpen(o => {
                // Opening marks everything read — the dot means "something
                // arrived you haven't looked at", nothing more.
                if (!o) onMarkNotificationsRead();
                return !o;
              });
            }}
            style={{ position: 'relative' }}
          >
            <Icon of={PortalIcons.action.notifications} size={16} />
            {/* Unread dot only when something is actually unread (it used to
                be hardcoded on, which made the bell cry wolf permanently). */}
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: 6, right: 7, width: 6, height: 6, borderRadius: '50%', background: 'var(--bad)', boxShadow: '0 0 0 2px var(--bg-1)' }} />
            )}
          </button>
          {notifOpen && (
            <div
              role="dialog"
              aria-label="Notifications"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                width: 320,
                maxHeight: 380,
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r, 6px)',
                zIndex: 30,
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                color: 'var(--text)',
                fontSize: 12.5,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>Notifications</span>
                <span style={{ flex: 1 }} />
                {notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={onClearNotifications}
                    style={{ background: 'transparent', border: 0, color: 'var(--muted)', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div style={{ overflowY: 'auto', padding: 6 }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '18px 12px', color: 'var(--muted)', textAlign: 'center' }}>
                    Nothing yet. Launches, saves and alerts from this session land here.
                  </div>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 'var(--r, 6px)' }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          marginTop: 4,
                          flex: '0 0 auto',
                          background: NOTIFICATION_KIND_COLOR[n.kind ?? 'info'] ?? 'var(--muted)',
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 600, lineHeight: 1.3 }}>{n.title}</span>
                        {n.sub && (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, overflowWrap: 'anywhere' }}>{n.sub}</span>
                        )}
                        <span style={{ display: 'block', fontSize: 10, color: 'var(--muted-2, var(--muted))', marginTop: 2 }}>{timeAgo(n.ts)}</span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div ref={profileRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="user-chip"
            onClick={() => setProfileOpen((o) => !o)}
            aria-haspopup="dialog"
            aria-expanded={profileOpen}
            style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
          >
            <div className="av amber">{user.initials}</div>
            <div style={{ textAlign: 'left' }}>
              <div className="name">{user.name}</div>
              <div className="role">{user.role}</div>
            </div>
          </button>
          {profileOpen && (
            <div
              role="dialog"
              aria-label="User profile"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                minWidth: 240,
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r, 6px)',
                padding: 12,
                zIndex: 30,
                display: 'grid',
                gap: 10,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                color: 'var(--text)',
                fontSize: 12.5,
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div className="av amber" style={{ width: 32, height: 32, fontSize: 12 }}>
                  {user.initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{user.name}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 2 }}>
                    {user.role}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--muted)',
                  borderTop: '1px solid var(--border)',
                  paddingTop: 8,
                  wordBreak: 'break-all',
                }}
              >
                {user.email}
              </div>
              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false);
                  signOut();
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--gold, #D4952B)',
                  border: '1px solid var(--gold, #D4952B)',
                  padding: '8px 14px',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}


export function StatusPill({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    certified: ['good', 'Certified'],
    inprogress: ['info', 'In Progress'],
    assigned: ['neutral', 'Assigned'],
    overdue: ['bad', 'Overdue'],
    flagged: ['warn', 'Flagged'],
    expiring: ['warn', 'Expiring ≤30d'],
    paused: ['warn', 'Paused'],
    published: ['good', 'Published'],
    'needs-review': ['warn', 'Needs Review'],
    draft: ['neutral', 'Draft'],
  };
  const [cls, label] = map[status] || ['neutral', status];
  return <span className={'pill ' + cls}>{label}</span>;
}

export function Toasts({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: string) => void }) {
  return (
    <div className="toasts">
      {toasts.map(t => (
        <div key={t.id} className={'toast ' + (t.kind || 'good')}>
          <div className="mk" />
          <div>
            <div style={{ fontWeight: 600 }}>{t.title}</div>
            {t.sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{t.sub}</div>}
          </div>
          {t.undo && (
            <button onClick={() => { t.undo!(); dismiss(t.id); }}>Undo</button>
          )}
          <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => dismiss(t.id)} aria-label="Dismiss">
            <Icon of={Icons.action.remove} size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

type CmdPaletteProps = {
  onClose: () => void;
  onNav: (v: ViewKey, payload?: unknown) => void;
  canOpen: (v: ViewKey) => boolean;
};
export function CmdPalette({ onClose, onNav, canOpen }: CmdPaletteProps) {
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);

  type NavEntry = { kind: 'nav'; label: string; key: NavKey; sub: string };
  type ActEntry = { kind: 'act'; label: string; sub: string; icon: IconSpec; run: () => void };

  const adminPages: NavEntry[] = (
    [
        { kind: 'nav', label: 'Live Sessions', key: 'live', sub: modChord('1') },
        { kind: 'nav', label: 'Workforce', key: 'workforce', sub: modChord('2') },
        { kind: 'nav', label: 'Orientation Assignment', key: 'cohort-builder', sub: modChord('3') },
        // No free digit chord left (1-0 are taken); reachable via ⌘K.
        { kind: 'nav', label: 'Cohorts', key: 'cohorts', sub: '' },
        { kind: 'nav', label: 'Review Queue', key: 'review-queue', sub: modChord('4') },
        { kind: 'nav', label: 'Leadership', key: 'leadership', sub: modChord('5') },
        { kind: 'nav', label: 'Orientations', key: 'orientations', sub: modChord('6') },
        { kind: 'nav', label: 'Orientation Editor', key: 'orientation-editor', sub: modChord('7') },
        { kind: 'nav', label: 'Members', key: 'members', sub: modChord('8') },
    ] satisfies NavEntry[]
  ).filter((p) => canOpen(p.key));
  const everyonePages: NavEntry[] = [
    { kind: 'nav', label: 'My Orientations', key: 'trainee', sub: modChord('0') },
    { kind: 'nav', label: 'Settings', key: 'settings', sub: modChord('9') },
  ];

  const ql = q.toLowerCase();
  const navHits = [...adminPages, ...everyonePages].filter(p =>
    p.label.toLowerCase().includes(ql),
  );
  const actions: ActEntry[] = (
    [
      { kind: 'act' as const, label: 'Toggle light / dark', sub: modChord('L'), icon: PortalIcons.action.themeDark, run: () => {} },
    ] satisfies ActEntry[]
  ).filter(a => a.label.toLowerCase().includes(ql));

  const all: Array<NavEntry | ActEntry> = [...navHits, ...actions];

  const pick = (n: number) => {
    const it = all[n];
    if (!it) return;
    if (it.kind === 'nav') onNav(it.key);
    onClose();
  };

  return (
    <div className="cmd-backdrop" onClick={onClose}>
      <div className="cmd" onClick={e => e.stopPropagation()}>
        <input
          autoFocus
          value={q}
          placeholder="Jump to a page…"
          onChange={e => { setQ(e.target.value); setIdx(0); }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { setIdx(i => Math.min(i + 1, all.length - 1)); e.preventDefault(); }
            else if (e.key === 'ArrowUp') { setIdx(i => Math.max(i - 1, 0)); e.preventDefault(); }
            else if (e.key === 'Enter') { pick(idx); }
            else if (e.key === 'Escape') { onClose(); }
          }}
        />
        <div className="results">
          {navHits.length > 0 && <div className="ghead">Pages</div>}
          {navHits.map((p, i) => (
            <div key={p.key} className={'row ' + (idx === i ? 'active' : '')} onClick={() => pick(i)}>
              <span className="ic"><Icon of={NAV_ICON[p.key]} size={12} /></span>{p.label}
              <span className="sub">{p.sub}</span>
            </div>
          ))}
          {actions.length > 0 && <div className="ghead">Actions</div>}
          {actions.map((a, i) => {
            const n = navHits.length + i;
            return (
              <div key={a.label} className={'row ' + (idx === n ? 'active' : '')} onClick={() => pick(n)}>
                <span className="ic"><Icon of={a.icon} size={12} /></span>{a.label}
                <span className="sub">{a.sub}</span>
              </div>
            );
          })}
          {all.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>No matches</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  const groups: Array<{ name: string; rows: Array<[string, string, string]> }> = [
    { name: 'Navigation', rows: [
      ['Live Sessions', MOD_LABEL, '1'],
      ['Workforce', MOD_LABEL, '2'],
      ['Orientation Assignment', MOD_LABEL, '3'],
      ['Review Queue', MOD_LABEL, '4'],
      ['Leadership', MOD_LABEL, '5'],
      ['Orientations', MOD_LABEL, '6'],
      ['Orientation Editor', MOD_LABEL, '7'],
      ['Members', MOD_LABEL, '8'],
      ['Settings', MOD_LABEL, '9'],
      ['My Orientations', MOD_LABEL, '0'],
    ]},
    { name: 'Global', rows: [
      ['Command palette', MOD_LABEL, 'K'],
      ['Toggle theme', MOD_LABEL, 'L'],
      ['Collapse / expand menu', MOD_LABEL, 'B'],
      ['Close dialog', 'Esc', ''],
    ]},
  ];

  return (
    <div className="kbd-overlay" onClick={onClose}>
      <div className="kbd-panel" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <h2>Keyboard shortcuts</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><Icon of={Icons.action.remove} size={14} /></button>
        </div>
        <div className="kbd-grid">
          {groups.map(g => (
            <div key={g.name}>
              <div className="label-up" style={{ marginBottom: 6 }}>{g.name}</div>
              {g.rows.map(r => (
                <div key={r[0]} className="kbd-row">
                  <span>{r[0]}</span>
                  <span className="keys">
                    {r[1] && <span className="kbd">{r[1]}</span>}
                    {r[2] && <span className="kbd">{r[2]}</span>}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
